// Studio: herunterladen, per Code verbinden, verbundene Geräte verwalten.

import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { Icon, errorText } from "../components/streaming-ui";
import { Callout, Card, Page, PageHeader } from "../components/ui";

const PLATFORM_LABEL: Record<string, string> = {
  "macos-universal": "macOS (Apple und Intel)",
  "macos-aarch64": "macOS (Apple-Chip)",
  "macos-x86_64": "macOS (Intel)",
  "windows-x86_64": "Windows",
  "linux-x86_64": "Linux (x86_64)",
  "linux-aarch64": "Linux (ARM)",
};

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return "windows";
  if (/Mac/i.test(ua)) return "macos";
  if (/Linux/i.test(ua)) return "linux";
  return "";
}

type Releases = Awaited<ReturnType<typeof api.getStudioReleases>>;
type Device = Awaited<ReturnType<typeof api.listStudioDevices>>[number];

export function StudioConnectPage(): JSX.Element {
  const [releases, setReleases] = useState<Releases>(undefined);
  const [devices, setDevices] = useState<Device[]>([]);
  const [code, setCode] = useState<{ code: string; expires_at: string; server_url: string } | null>(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([api.getStudioReleases().catch(() => undefined), api.listStudioDevices()]);
      setReleases(r);
      setDevices(d);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);
  useEffect(() => void load(), [load]);

  // Solange ein Code angezeigt wird: Restzeit zählen und nachsehen, ob das Studio sich verbunden hat.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => {
      setNow(Date.now());
      void api.listStudioDevices().then((d) => {
        if (d.length > devices.length) {
          setDevices(d);
          setCode(null);
        }
      }).catch(() => undefined);
    }, 2000);
    return () => clearInterval(t);
  }, [code, devices.length]);

  const secondsLeft = code ? Math.max(0, Math.round((new Date(code.expires_at).getTime() - now) / 1000)) : 0;
  const mine = detectPlatform();
  const files = releases ? Object.entries(releases.files).sort(([a], [b]) => Number(b.startsWith(mine)) - Number(a.startsWith(mine))) : [];

  return (
    <Page className="max-w-5xl">
      <PageHeader
        eyebrow="Sendebetrieb"
        title="Studio"
        description="Das Studio ist das Programm für Live-Sendungen: zwei Player, Cartwall, Mikrofon mit gutem Klang und ein Knopf zum Senden. Es holt Musik und Jingles aus der Suite und geht über den Live-Eingang eures Kanals auf Sendung."
      />
      <div className="space-y-6">
      {error && <Callout tone="danger">{error}</Callout>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <StepCard nr={1} title="Herunterladen" done={false}>
        {files.length === 0 ? (
          <p className="text-sm leading-relaxed text-muted">
            Auf diesem Server liegt noch kein Studio-Paket. Wer die Installation betreibt, kann es mit <code className="rounded bg-input px-1 py-0.5 font-mono text-xs text-primary ring-1 ring-[var(--border)]">scripts/publish-studio.sh</code> bereitstellen.
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {files.map(([platform, f], i) => (
                <a
                  key={platform}
                  href={f.url}
                  className={i === 0 && platform.startsWith(mine) ? "btn btn-primary btn-lg justify-between" : "btn btn-secondary justify-between"}
                >
                  <span>{PLATFORM_LABEL[platform] ?? platform}</span>
                  <span className="tabular-nums opacity-80">{(f.size / 1_048_576).toFixed(0)} MB</span>
                </a>
              ))}
            </div>
            <p className="text-xs leading-relaxed text-muted">
              Version {releases?.version}. Das Studio aktualisiert sich später selbst. macOS: ZIP öffnen, „WR Studio“ in den Programme-Ordner ziehen,
              beim ersten Start mit Rechtsklick → Öffnen bestätigen.
            </p>
          </>
        )}
      </StepCard>

      <StepCard nr={2} title="Verbinden" done={devices.length > 0 && !code}>
        <p className="text-sm leading-relaxed text-muted">Im Studio erscheint beim Start das Feld „Kopplungscode“ – dort diese beiden Angaben eintippen. Ein Passwort braucht das Studio nicht.</p>
        {code && secondsLeft > 0 ? (
          <div className="well space-y-3 !p-4">
            <div>
              <div className="text-xs font-medium text-muted">Suite-Adresse</div>
              <div className="mt-0.5 break-all font-mono text-sm text-primary">{code.server_url}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted">Code</div>
              <div className="mt-1 font-mono text-4xl font-semibold tracking-[0.25em] text-brand-primary">{code.code}</div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-primary" aria-hidden="true" />
              <span className="tabular-nums">gilt noch {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, "0")} Minuten, nur einmal</span>
            </div>
          </div>
        ) : (
          <button onClick={() => void api.createPairingCode().then(setCode).catch((err) => setError(errorText(err)))}
            className="btn btn-primary self-start">
            Code erzeugen
          </button>
        )}
      </StepCard>
      </div>

      <Card title="Verbundene Studios" actions={devices.length > 0 ? <span className="badge badge-neutral tabular-nums">{devices.length}</span> : undefined} padded={devices.length === 0}>
        {devices.length === 0 ? (
          <p className="text-sm text-muted">Noch keins.</p>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {devices.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-input text-secondary ring-1 ring-[var(--border)]"><Icon name="server" /></span>
                <span className="min-w-0">
                  <span className="block font-medium text-primary">{d.name}</span>
                  <span className="block text-xs text-muted">{d.user_name ?? d.user_email}{d.platform ? ` · ${PLATFORM_LABEL[d.platform] ?? d.platform}` : ""}{d.app_version ? ` · ${d.app_version}` : ""}</span>
                </span>
                <span className="ml-auto text-xs tabular-nums text-muted">{d.last_seen_at ? `zuletzt ${new Date(d.last_seen_at).toLocaleString("de-DE")}` : ""}</span>
                <button
                  onClick={() => window.confirm(`„${d.name}" trennen? Das Studio muss danach neu verbunden werden.`) && void api.revokeStudioDevice(d.id).then(load).catch((err) => setError(errorText(err)))}
                  className="btn btn-ghost btn-sm text-red-600 dark:text-red-400"
                >
                  Trennen
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      </div>
    </Page>
  );
}

/** Ein Schritt mit großer Nummer – erledigte Schritte bekommen einen Haken. */
function StepCard({ nr, title, done, children }: { nr: number; title: string; done: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <section className="card flex flex-col gap-4 p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${done ? "bg-emerald-500 text-white" : "bg-brand-primary/10 text-brand-primary ring-1 ring-brand-primary/25"}`}>
          {done ? <Icon name="check" /> : nr}
        </span>
        <h2 className="text-[0.95rem] font-semibold text-primary">{title}</h2>
      </div>
      {children}
    </section>
  );
}
